import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ChevronRight, 
  ChevronDown,
  Phone, 
  ArrowRight,
  Shield,
  Calendar,
  Menu,
  X,
  ChevronUp,
  ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEOHead, generateFAQSchema, breadcrumbSchema } from "@/components/SEOHead";

const NBR_LOGO = "https://nationalbusinessregister.co.uk/wp-content/uploads/2024/11/logo.png";
const BOOKINGS_URL = "https://bookings.cloud.microsoft/book/NationalBusinessRegisterGroupLimitedNBR@nbrg.co.uk/s/mbmhEOVCOEG72cEdq6hN4Q2?ismsaljsauthenabled=true";

const FAQ_CATEGORIES = [
  {
    title: "About the Service",
    faqs: [
      {
        q: "What is the difference between trade mark monitoring and enforcement?",
        a: "Trade mark monitoring is our proactive watching service that alerts clients when potentially conflicting marks are filed. Enforcement is the action taken after a conflict is identified — such as filing an opposition, sending cease and desist letters, or pursuing legal action. NBR Trade Mark Monitoring handles the monitoring side; our team at National Business Register can also assist with enforcement if needed."
      },
      {
        q: "Is trade mark monitoring legally required?",
        a: "No, trade mark monitoring is not legally mandatory. However, trade mark owners have a responsibility to protect their marks — failure to act against infringers can weaken rights over time. We recommend monitoring as a best practice for any business serious about brand protection."
      },
      {
        q: "Who is this service designed for?",
        a: "Our service is designed for UK trade mark owners and SMEs with a portfolio of registered trade marks. Whether you hold a single mark or manage dozens, we provide the tools to keep your brands protected. We also support in-house legal teams, IP managers, and growing businesses expanding their brand portfolio."
      },
      {
        q: "What trade marks can we monitor?",
        a: "We can monitor any UK registered trade mark or pending application. This includes word marks, logos, and combined marks. Clients can import directly from UK IPO data or add marks manually through the dashboard."
      }
    ]
  },
  {
    title: "How It Works",
    faqs: [
      {
        q: "How often are trade marks monitored?",
        a: "We scan the UK IPO journal weekly when new publications are released."
      },
      {
        q: "What happens if a conflicting trade mark is found?",
        a: "We send an instant alert with a detailed similarity analysis. Clients can review the application in their dashboard, consult with our trade mark specialists for advice, and if appropriate, we can assist with filing a formal opposition within the 2-month deadline."
      },
      {
        q: "How quickly will we alert clients to potential conflicts?",
        a: "Alerts are generated immediately after our weekly scan of new UK IPO journal publications. For urgent matters, clients can also run manual scans at any time through their dashboard."
      },
      {
        q: "What types of similarity does the system detect?",
        a: "Our pattern-based analysis detects identical marks, confusingly similar names, phonetic similarities (marks that sound alike), visual similarities (logo comparisons), conceptual similarities (marks with similar meanings), and conflicts within relevant goods and services classifications."
      }
    ]
  },
  {
    title: "Registry Coverage",
    faqs: [
      {
        q: "Which registries does the service currently cover?",
        a: "We currently provide full monitoring coverage for the UK Intellectual Property Office (UK IPO). This includes all new trade mark applications and publications in the UK IPO journal."
      },
      {
        q: "Are new registries coming soon?",
        a: "Yes, we are actively working on expanding our coverage to include the European Union Intellectual Property Office (EUIPO) and the World Intellectual Property Organisation (WIPO). We will announce availability dates as these integrations are finalised. Existing clients will be notified first."
      },
      {
        q: "Will adding EU or international coverage affect my pricing?",
        a: "Each registry will be priced separately. UK IPO monitoring pricing remains as listed. When EUIPO and WIPO coverage becomes available, we will publish dedicated pricing for those registries. Clients will be able to choose which registries to monitor based on their needs."
      },
      {
        q: "If I need UK and EU coverage, will my pricing double?",
        a: "Not necessarily. While each registry is priced independently, we plan to offer bundled pricing for clients who monitor across multiple registries. Our aim is to make comprehensive coverage accessible and cost-effective. Details will be shared when EU coverage launches."
      }
    ]
  },
  {
    title: "Pricing & Billing",
    faqs: [
      {
        q: "How does volume-based pricing work?",
        a: "Our pricing rewards portfolio-wide protection. The more trade marks you monitor, the lower the per-mark cost. We offer tiered discounts: 10% off for 2-5 marks, 20% off for 6-10 marks, and 30% off for 11+ marks. The discounted rate applies to all marks in the portfolio, not just additional ones."
      },
      {
        q: "Can we cancel our subscription at any time?",
        a: "Yes, clients can cancel their subscription at any time with no penalty. Access continues until the end of the current billing period."
      },
      {
        q: "Is there an annual payment option?",
        a: "Yes, we offer a further 10% discount for clients who choose to pay annually in advance. This is calculated on top of any volume-based discount, providing the best possible value."
      }
    ]
  },
  {
    title: "Support & Enforcement",
    faqs: [
      {
        q: "Do we offer support if a client needs to oppose an application?",
        a: "Yes. National Business Register has over 40 years of IP expertise. We offer free initial consultations to discuss opposition strategies and enforcement options. Our team can handle the entire opposition process on behalf of clients if required."
      },
      {
        q: "How do we get started?",
        a: "Getting started is straightforward. Complete our intake form with your trade mark details, and our team will set up your monitoring account. We also offer free demonstrations — book a session to see the platform in action before committing."
      }
    ]
  }
];

const FAQAccordion = ({ faq, index }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      className="border border-slate-200 rounded-xl overflow-hidden hover:border-teal-200 transition-colors"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-5 sm:p-6 text-left bg-white hover:bg-slate-50 transition-colors"
        data-testid={`faq-toggle-${index}`}
      >
        <h3 className="font-bold text-slate-900 pr-4 text-sm sm:text-base">{faq.q}</h3>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-teal-600 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="px-5 sm:px-6 pb-5 sm:pb-6 bg-slate-50"
        >
          <p className="text-slate-600 leading-relaxed text-sm sm:text-base">{faq.a}</p>
        </motion.div>
      )}
    </motion.div>
  );
};

export const FAQPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const allFaqs = FAQ_CATEGORIES.flatMap(cat => cat.faqs);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Frequently Asked Questions"
        description="Find answers about NBR Trade Mark Monitoring: how it works, pricing, UK registry coverage, opposition support, and how we help protect your registered trade marks."
        path="/faq"
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            generateFAQSchema(allFaqs),
            breadcrumbSchema([
              { name: "Home", url: "/landing" },
              { name: "FAQs", url: "/faq" }
            ])
          ]
        }}
      />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/landing" className="flex items-center gap-3">
              <img src={NBR_LOGO} alt="National Business Register" className="h-8 sm:h-10 w-auto" />
              <div className="hidden sm:block">
                <span className="font-heading font-black text-lg text-slate-900">NBR</span>
                <span className="text-xs text-teal-600 tracking-widest uppercase ml-1">Trade Mark Monitoring</span>
              </div>
            </Link>
            
            <div className="hidden md:flex items-center gap-6">
              <Link to="/landing" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">Home</Link>
              <Link to="/landing#methodology" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">Features</Link>
              <Link to="/landing#how-it-works" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">How It Works</Link>
              <Link to="/landing#pricing" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">Pricing</Link>
              <Link to="/about" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">About You</Link>
              <Link to="/faq" className="text-sm text-teal-600 font-medium">FAQs</Link>
              <Link to="/blog" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">Blog</Link>
              <a href="tel:08000699090" className="text-sm text-slate-600 hover:text-teal-600 flex items-center gap-1 transition-colors">
                <Phone className="w-4 h-4" />
                0800 069 9090
              </a>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3">
              <Link to="/register-trademarks">
                <Button className="bg-teal-600 hover:bg-teal-700 text-white text-sm px-3 sm:px-4">
                  Get Started
                </Button>
              </Link>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-slate-600 hover:text-teal-600 transition-colors"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
        
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 shadow-lg px-4 py-4 space-y-3">
            <Link to="/landing" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-slate-600 hover:text-teal-600 font-medium">Home</Link>
            <Link to="/landing#methodology" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-slate-600 hover:text-teal-600 font-medium">Features</Link>
            <Link to="/landing#how-it-works" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-slate-600 hover:text-teal-600 font-medium">How It Works</Link>
            <Link to="/landing#pricing" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-slate-600 hover:text-teal-600 font-medium">Pricing</Link>
            <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-slate-600 hover:text-teal-600 font-medium">About You</Link>
            <Link to="/blog" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-slate-600 hover:text-teal-600 font-medium">Blog</Link>
            <a href={BOOKINGS_URL} target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-slate-600 hover:text-teal-600 font-medium">Book a Demo</a>
            <a href="tel:08000699090" className="flex items-center gap-2 py-2 text-teal-600 font-medium">
              <Phone className="w-4 h-4" /> 0800 069 9090
            </a>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="pt-24 sm:pt-32 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 text-sm text-slate-500 mb-4">
            <Link to="/landing" className="hover:text-teal-600">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-900">FAQs</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-black text-slate-900 mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Everything you need to know about our trade mark monitoring service, pricing, and how we help protect your brand.
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-3xl mx-auto">
          {FAQ_CATEGORIES.map((category, catIdx) => (
            <div key={catIdx} className="mb-10">
              <h2 className="text-lg sm:text-xl font-heading font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-teal-600" />
                {category.title}
              </h2>
              <div className="space-y-3">
                {category.faqs.map((faq, faqIdx) => (
                  <FAQAccordion key={faqIdx} faq={faq} index={catIdx * 10 + faqIdx} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-teal-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-heading font-black text-white mb-4">
            Still Have Questions?
          </h2>
          <p className="text-teal-100 mb-8 max-w-2xl mx-auto">
            Our team is happy to help. Book a free consultation or call us directly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => window.open(BOOKINGS_URL, '_blank')}
              className="bg-white text-teal-700 hover:bg-slate-100 px-8 py-6"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Book Free Consultation
            </Button>
            <a href="tel:08000699090">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6">
                <Phone className="w-4 h-4 mr-2" />
                Call 0800 069 9090
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <img src={NBR_LOGO} alt="National Business Register" className="h-14 w-auto brightness-0 invert" />
              <div>
                <span className="font-heading font-black text-lg text-white">NBR</span>
                <span className="text-xs text-teal-400 tracking-widest uppercase ml-1">Trade Mark Monitoring</span>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-400">
              <a href="https://nationalbusinessregister.co.uk/" target="_blank" rel="noopener noreferrer" className="hover:text-teal-400 transition-colors flex items-center gap-1">
                nationalbusinessregister.co.uk <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <p className="text-slate-400 text-sm text-center">
              &copy; {new Date().getFullYear()} National Business Register Group Ltd.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FAQPage;
